import React from "react";
import { useLocation } from "react-router-dom";
import { SidebarProvider, SidebarInset, Sidebar, SidebarHeader, SidebarContent, SidebarFooter, SidebarRail } from "@repo/ui/primitives/sidebar";
import { PageSkeleton } from "@/components/common/PageSkeleton";
import { useAuthStore, useTenantStore } from "@/store";
import { SISMSidebar } from "@/components/sidebar/Sidebar";
import { Header } from "@/components/layout/Header";
import { authIntegration } from "@/lib/api/client";
import { useSidebarNavigation } from "@/hooks/useSidebarNavigation.js";
import { getDockPreferenceKey } from "@/store/dock-preferences.store.js";

function AuthenticatedLoadingShell({ user, onLogout, isAboutSgrc }) {
    const selectedTenantId = useTenantStore((state) => state.selectedTenantId);
    const preferenceScopeKey = React.useMemo(
        () => getDockPreferenceKey(user, selectedTenantId),
        [user, selectedTenantId],
    );
    const navigation = useSidebarNavigation(preferenceScopeKey, { preferencesReady: false });

    return (
        <SidebarProvider>
            <SISMSidebar
                data={navigation.data}
                allPages={navigation.allPages}
                availableModules={navigation.availableModules}
                activeWorkspace={navigation.activeWorkspace}
                customWorkspaces={navigation.customWorkspaces}
                favoritePages={navigation.favoritePages}
                favoriteUrls={navigation.favoriteUrls}
                onSelectWorkspace={navigation.selectWorkspace}
                navigationReady={false}
            />
            <SidebarInset className="flex h-dvh flex-col overflow-hidden bg-background">
                <Header user={user} onLogout={onLogout} />

                <div className={`app-shell-content flex flex-1 flex-col bg-background ${isAboutSgrc ? "p-0" : "px-[var(--ds-space-page-gutter-mobile)] pb-[var(--ds-space-8)] pt-[var(--ds-space-6)] sm:px-[var(--ds-space-page-gutter-compact)] lg:px-[var(--ds-space-page-gutter)]"}`}>
                    <div className="h-full overflow-hidden">
                        <PageSkeleton />
                    </div>
                </div>
            </SidebarInset>
        </SidebarProvider>
    );
}

export function LoadingLayout() {
    const { user } = useAuthStore();
    const location = useLocation();
    const isAboutSgrc = location.pathname === "/user-manual/about-sgrc";

    const handleLogout = async () => {
        await authIntegration.logout();
    };

    // If we have a user (even if refreshing), show the real layout structure
    // so the sidebar/header don't flicker or change to skeletons.
    if (user) {
        return <AuthenticatedLoadingShell user={user} onLogout={handleLogout} isAboutSgrc={isAboutSgrc} />;
    }

    // Fallback for truly unauthenticated state (should rarely be seen due to redirects)
    return (
        <SidebarProvider>
            <Sidebar collapsible="icon">
                <SidebarHeader className="h-[var(--ds-header-height)] min-h-[var(--ds-header-height)] border-b border-sidebar-border bg-sidebar p-0">
                    <div className="flex h-full w-full items-center justify-start px-3">
                        <div className="flex h-full w-full items-center justify-start gap-3 p-3">
                            <div className="size-10 animate-pulse rounded-[var(--ds-radius-control)] bg-sidebar-accent/50 motion-reduce:animate-none" />
                            <div className="h-6 w-32 animate-pulse rounded-[var(--ds-radius-control-compact)] bg-sidebar-accent/50 motion-reduce:animate-none" />
                        </div>
                    </div>
                </SidebarHeader>

                <SidebarContent className="custom-scrollbar px-2 pr-4">
                    <div className="space-y-[var(--ds-space-4)] p-2">
                        {[1, 2, 3].map((group) => (
                            <div key={group} className="space-y-[var(--ds-space-2)]">
                                <div className="ml-2 h-4 w-20 animate-pulse rounded-[var(--ds-radius-control-compact)] bg-sidebar-accent/30 motion-reduce:animate-none" />
                                {[1, 2, 3].map((item) => (
                                    <div key={item} className="h-8 w-full animate-pulse rounded-[var(--ds-radius-control-compact)] bg-sidebar-accent/20 motion-reduce:animate-none" />
                                ))}
                            </div>
                        ))}
                    </div>
                </SidebarContent>

                <SidebarFooter className="border-t border-sidebar-border bg-sidebar p-3">
                    <div className="flex w-full items-center justify-between">
                        <div className="size-8 animate-pulse rounded-[var(--ds-radius-control)] bg-sidebar-accent/50 motion-reduce:animate-none" />
                        <div className="size-8 animate-pulse rounded-[var(--ds-radius-control)] bg-sidebar-accent/50 motion-reduce:animate-none" />
                    </div>
                </SidebarFooter>

                <SidebarRail />
            </Sidebar>

            <SidebarInset className="flex h-dvh flex-col overflow-hidden bg-background">
                <header className="flex h-[var(--ds-header-height)] min-h-[var(--ds-header-height)] shrink-0 items-center justify-between gap-2 border-b border-sidebar-border bg-sidebar px-6 text-sidebar-foreground">
                    <div className="flex w-full items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                            <div className="size-8 rounded-[var(--ds-radius-control)] bg-muted md:hidden" />
                            <div className="hidden h-10 w-40 animate-pulse rounded-[var(--ds-radius-control)] bg-muted/50 motion-reduce:animate-none md:block" />
                        </div>

                        <div className="flex items-center gap-3">
                            <div className="size-8 animate-pulse rounded-full bg-muted/50 motion-reduce:animate-none" />
                            <div className="size-8 animate-pulse rounded-full bg-muted/50 motion-reduce:animate-none" />
                            <div className="size-8 animate-pulse rounded-full bg-muted/50 motion-reduce:animate-none" />

                            <div className="ml-2 flex items-center gap-3">
                                <div className="size-8 animate-pulse rounded-full bg-muted/60 motion-reduce:animate-none" />
                                <div className="hidden flex-col gap-1 md:flex">
                                    <div className="h-3 w-24 animate-pulse rounded-[var(--ds-radius-control-compact)] bg-muted/60 motion-reduce:animate-none" />
                                    <div className="h-2 w-32 animate-pulse rounded-[var(--ds-radius-control-compact)] bg-muted/40 motion-reduce:animate-none" />
                                </div>
                            </div>
                        </div>
                    </div>
                </header>

                <div className="flex flex-1 flex-col bg-background">
                    <div className={`app-shell-content overflow-y-auto overflow-x-hidden ${isAboutSgrc ? "p-0" : "px-[var(--ds-space-page-gutter-mobile)] pb-[var(--ds-space-8)] pt-[var(--ds-space-6)] sm:px-[var(--ds-space-page-gutter-compact)] lg:px-[var(--ds-space-page-gutter)]"}`}>
                        <PageSkeleton />
                    </div>
                </div>
            </SidebarInset>
        </SidebarProvider>
    );
}
